import React from 'react'
import isNil from 'lodash/isNil'
import {
  Modal,
  Form,
  Select,
  Input,
  message,
} from '@surecompanies/core_components'
import styled from 'styled-components'
import {
  PaymentCase,
  Priority,
  PRIORITY_LABELS,
  PriorityList,
} from '../Utils/types'
import { usePaymentAssignmentUserOptions } from '../hooks/usePaymentAssignmentUserOptions'
import { PaymentAssignmentRole } from '../Utils/roles'

const ModalSection = styled.div`
  margin-bottom: 24px;
`

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #3a4951;
  margin-bottom: 12px;
`

const SummaryText = styled.div`
  font-size: 14px;
  color: #3a4951;
  margin-bottom: 8px;
`

const CarriersList = styled.div`
  font-size: 14px;
  color: #677786;
  margin-top: 8px;
`

interface BulkAssignmentModalProps {
  visible: boolean
  selectedCases: PaymentCase[]
  onClose: () => void
  onAssign: (
    assigneeId: number | null,
    escalatorId: number | null,
    priority: Priority | null,
    notes: string
  ) => void | Promise<void>
}

export default function BulkAssignmentModal({
  visible,
  selectedCases,
  onClose,
  onAssign,
}: BulkAssignmentModalProps) {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = React.useState(false)
  const watchedAssigneeId = Form.useWatch('assigneeId', form)
  const watchedEscalatorId = Form.useWatch('escalatorId', form)
  const watchedPriority = Form.useWatch('priority', form)
  const canSubmit =
    !isNil(watchedAssigneeId) ||
    !isNil(watchedEscalatorId) ||
    !isNil(watchedPriority)
  const { options: makerOptions, isLoading: makersLoading } =
    usePaymentAssignmentUserOptions(PaymentAssignmentRole.MAKER)
  const { options: escalatorOptions, isLoading: escalatorsLoading } =
    usePaymentAssignmentUserOptions(PaymentAssignmentRole.ESCALATOR)

  const carriers = React.useMemo(() => {
    const names = selectedCases
      .map(
        (c) =>
          c.paymentCarrier?.name ??
          c.carrierName ??
          c.benefit?.carrierName ??
          ''
      )
      .filter((name) => name !== '')
    return Array.from(new Set(names))
  }, [selectedCases])

  const handleCancel = () => {
    if (submitting) return
    form.resetFields()
    onClose()
  }

  const handleSubmit = async () => {
    if (!canSubmit) {
      message.error(
        'Please select at least one of Maker, Escalator, or Priority'
      )
      return
    }
    try {
      const values = await form.validateFields()
      const assigneeIdValue = isNil(values.assigneeId)
        ? null
        : Number(values.assigneeId)
      const escalatorIdValue = isNil(values.escalatorId)
        ? null
        : Number(values.escalatorId)
      const priorityValue = isNil(values.priority)
        ? null
        : (values.priority as Priority)
      setSubmitting(true)
      await onAssign(
        assigneeIdValue,
        escalatorIdValue,
        priorityValue,
        values.notes || ''
      )
      form.resetFields()
      onClose()
    } catch (errorInfo) {
      // Validation failed (or submit threw) - validation is already shown by form
      console.log('Bulk assignment submit failed:', errorInfo)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="Bulk Assignment"
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okButtonProps={{ disabled: !canSubmit }}
      confirmLoading={submitting}
      width={500}
      destroyOnClose
    >
      {/* Summary Section */}
      <ModalSection>
        <SectionTitle>Summary</SectionTitle>
        <SummaryText>
          Total Cases Selected: <strong>{selectedCases.length}</strong>
        </SummaryText>
        <CarriersList>Carriers Involved: {carriers.join(', ')}</CarriersList>
      </ModalSection>

      {/* Assignment Controls */}
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="assigneeId" label="Assign Maker To">
          <Select
            allowClear
            placeholder="Select maker (optional)"
            loading={makersLoading}
            showSearch
            optionFilterProp={'data-search'}
          >
            {makerOptions.map((u) => (
              <Select.Option
                key={u.id}
                value={u.id}
                data-search={`${u.name} ${u.role}`}
              >
                {u.name} ({u.role})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="escalatorId" label="Assign Escalator To">
          <Select
            allowClear
            placeholder="Select escalator (optional)"
            loading={escalatorsLoading}
            showSearch
            optionFilterProp={'data-search'}
          >
            {escalatorOptions.map((u) => (
              <Select.Option
                key={u.id}
                value={u.id}
                data-search={`${u.name} ${u.role ?? ''}`}
              >
                {u.name} ({u.role ?? 'NO_ROLE'})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="priority" label="Priority">
          <Select allowClear placeholder="Select Priority (optional)">
            {PriorityList.map((priority) => (
              <Select.Option key={priority} value={priority}>
                {PRIORITY_LABELS[priority]}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="notes" label="Notes">
          <Input.TextArea
            rows={4}
            placeholder="Add notes about this assignment (optional)..."
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
